import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import { Header } from "./components/Header";
import { Hero } from "./components/Hero";
import { ProductShowcase } from "./components/ProductShowcase";
import { FeaturesSection } from "./components/FeaturesSection";
import { Footer } from "./components/Footer";
import { SEOHead } from "./components/SEOHead";
import { useContentData } from "./hooks/useContentData";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/admin/ProtectedRoute";
import { AdminLogin } from "./components/admin/AdminLogin";
import { AdminLayout } from "./components/admin/AdminLayout";
import { Dashboard } from "./components/admin/Dashboard";
import { ProductsManager } from "./components/admin/ProductsManager";
import { HeroManager } from "./components/admin/HeroManager";
import { FeaturesManager } from "./components/admin/FeaturesManager";
import { GalleryManager } from "./components/admin/GalleryManager";
import { SettingsManager } from "./components/admin/SettingsManager";
import { ContactManager } from "./components/admin/ContactManager";
import { FooterManager } from "./components/admin/FooterManager";
import { TopNavManager } from "./components/admin/TopNavManager";
import { SEOManager } from "./components/admin/SEOManager";
import { SupportManager } from "./components/admin/SupportManager";
import { RelicCodesDashboard } from "./components/admin/RelicCodesDashboard";
import { RelicsContentDashboard } from "./components/admin/RelicsContentDashboard";
import { ProductDescriptionManager } from "./components/admin/ProductDescriptionManager";
import { RelicsPage } from "./components/RelicsPage";
import { ProductDescriptionPage } from "./components/ProductDescriptionPage";

function LandingPage() {
  const { data, loading, error } = useContentData();
  
  // Get first product and its first variant as default
  const firstProduct = data.products?.[0];
  const [selectedVariant, setSelectedVariant] = useState(null);
  
  // Set default variant when data loads
  React.useEffect(() => {
    if (firstProduct && !selectedVariant) {
      setSelectedVariant(firstProduct.variants?.[0]);
    }
  }, [firstProduct, selectedVariant]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-red-500 text-xl">Error loading content. Please refresh.</div>
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-white">
      <SEOHead seoData={data.seo} productData={data.products} settings={data.settings} />
      {/* Google Tag Manager (noscript) */}
      {data.settings?.gtm_id && (
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${data.settings.gtm_id}`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          />
        </noscript>
      )}
      <Header topnavData={data.topnav} settings={data.settings} />
      <Hero heroData={data.hero} settings={data.settings} />
      <ProductShowcase 
        product={firstProduct}
        selectedVariant={selectedVariant} 
        setSelectedVariant={setSelectedVariant} 
      />
      <FeaturesSection 
        featuresData={data.features}
        galleryData={data.gallery}
        selectedColor={selectedVariant?.color || '#8B5CF6'} 
      />
      <Footer footerData={data.footer} settings={data.settings} />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Landing Page */}
          <Route path="/" element={<LandingPage />} />
          
          {/* Public Relics Page */}
          <Route path="/relics" element={<RelicsPage />} />

          {/* Public Product Description Page */}
          <Route path="/product-description" element={<ProductDescriptionPage />} />

          
          {/* Admin Login */}
          <Route path="/admin/login" element={<AdminLogin />} />
          
          {/* Protected Admin Routes */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Dashboard />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/products"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <ProductsManager />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/topnav"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <TopNavManager />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/hero"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <HeroManager />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/features"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <FeaturesManager />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/gallery"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <GalleryManager />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/contact"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <ContactManager />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <SettingsManager />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/footer"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <FooterManager />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/support"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <SupportManager />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/seo"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <SEOManager />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/relic-codes"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <RelicCodesDashboard />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/relics-content"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <RelicsContentDashboard />
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/product-description"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <ProductDescriptionManager />
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          
          {/* Redirect /admin to /admin/dashboard */}
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          
          {/* 404 redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
